import {alpha, Grid, Skeleton} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import React, {FC, useMemo} from "react";
import {getRandomStepsArray, IStep, IStepNormalized} from "../../../store/how-it-works/Helper";
import {getRandomNumber} from "../../../util/utils";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import MarkdownWrapper from "../../shared/markdown-wrapper/MarkdownWrapper";

interface IProps {
    steps: IStep[]
    loading: boolean
    type: "" | "alternative_"
}

const Steps: FC<IProps> = (props) => {
    const {i18n} = useTranslation()
    const theme = useTheme()

    const stepsNormalized: IStepNormalized[] = useMemo(() => {
        const lang = i18n.language;

        const getValue = (step: any, key: string) => {
            const specific = step[`${props.type}${key}_${lang}`];
            const fallback = props.type === "alternative_" ? step[`${key}_${lang}`] : null;
            return specific ?? fallback ?? null;
        };

        return props.steps
            .map((s) => ({
                title: getValue(s, "title"),
                subtitle: getValue(s, "subtitle"),
                gif: getValue(s, "gif"),
            }))
            .filter((s) => !!s.title);
    }, [props.steps, props.type, i18n.language]);

    const textGrid = (index: number, item: IStepNormalized) => (
        <Grid container item xs={6} flexDirection="column" justifyContent="center">
            <Typography color="primary" variant="h4" fontSize="1.875rem"
                        fontWeight={600}>
                {props.loading ?
                    <Skeleton width={`${getRandomNumber(100, 150)}px`} animation='wave'/> :
                    `${index + 1}. ${item.title}`}
            </Typography>
            <MarkdownWrapper
                source={item.subtitle}
                loading={props.loading} sx={{mt: 2}}/>
        </Grid>
    )

    const gifGrid = (s: IStepNormalized) => (
        <Grid item container xs={6} alignItems="center" justifyContent="center">
            {props.loading ?
                <Skeleton variant="rectangular" animation='pulse' width='100%' height='100%'/> :
                <Box bgcolor={alpha(theme.palette.primary.main, .1)} p="1em" borderRadius='0.5em'
                     sx={{backdropFilter: 'blur(5px)'}}>
                    <img src={s.gif}
                         alt={s.title} height="auto"
                         width="100%"
                         style={{borderRadius: '0.5em', display: 'block'}}/>
                </Box>}
        </Grid>
    )

    return (
        <Grid container spacing={5}>
            {(props.loading ? getRandomStepsArray() : stepsNormalized).map((s, idx) => (
                <React.Fragment key={s.title}>
                    {idx % 2 === 0 ? textGrid(idx, s) : gifGrid(s)}
                    {idx % 2 === 0 ? gifGrid(s) : textGrid(idx, s)}
                </React.Fragment>
            ))}
        </Grid>
    )
}

export default Steps
